import { useEffect } from 'react';
import { findMostSpecificRoute } from 'quartz-routing/internal/routeMatchingUtils';
import { onPopstateCaptured } from 'quartz-routing/internal/popstate';
import { syncHistoryToBrowser, findBasePath } from 'quartz-routing/initialize';
import { createNestedHistory } from 'quartz-routing/createNestedHistory';
import { set } from 'quartz/globalState/api';
import { HISTORY_INSTANCE, HISTORY_BASE_PATH } from 'quartz-routing/globalState';
import { getCurrentPackageName } from 'quartz/framework/environment/service';
let activeApp = null;
export function _deactivateActiveApp() {
  if (activeApp) {
    activeApp.historyUnlisten();
    activeApp = null;
  }
}
export function createAttachHistory(appName, browserHistory) {
  return (remoteHistory, basename) => {
    // guard against unnecessary re-attaches of the same history instance
    if (activeApp && activeApp.history === remoteHistory) {
      return;
    }

    // Host deactivates the previous app immediately — the nested app has no
    // control over when the host stops listening.
    _deactivateActiveApp();

    // remoteHistory is always a NestedHistory created by
    // initializeNestedHistory — the cast is safe.
    const historyUnlisten = syncHistoryToBrowser(remoteHistory, browserHistory);
    activeApp = {
      history: remoteHistory,
      historyUnlisten,
      appName,
      basename
    };
  };
}
export function _getActiveApp() {
  return activeApp;
}
export function _resetForTesting() {
  _deactivateActiveApp();
}

/**
 * Syncs the active nested app's NestedHistory location on browser
 * Back/Forward events.
 *
 * Scenario: the user navigates Back between sub-pages within the same app
 * (e.g. Back between record sub-routes in crm-records-ui). The browser URL
 * changes via the DOM history API, but NestedHistory has no popstate
 * listener — nothing would update the app's React Router location and the
 * app would be stuck showing the previous sub-page. This hook intercepts
 * popstate in capture phase and calls history.replace() on the active app so
 * its router re-renders to the correct sub-page before the host Switch
 * re-renders.
 *
 * For cross-app navigation the host's createBrowserHistory update causes the
 * Switch to unmount/mount the right app; no nested history sync is needed.
 */
// Wrapped in an object so tests can spy on window.location access from within this module.
export const _graniteNav = {
  pathname() {
    return window.location.pathname;
  },
  search() {
    return window.location.search;
  },
  href() {
    return window.location.href;
  },
  fullPageNavigate(url) {
    window.location.assign(url);
  }
};
export function usePopstateCapture(localRouteOverrides) {
  useEffect(() => {
    const handler = () => {
      const newPathname = _graniteNav.pathname();
      const matchedKey = findMostSpecificRoute(Object.keys(localRouteOverrides), newPathname);
      if (!matchedKey) {
        // if there was no match that means that the remote previously rendered something
        // at this URL, but the URL is not actually in our localRouteOverrides. A side-effect
        // of our current naive approach of hardcoding routing for remotes.
        // force a full page load if this happens.
        // This will most likely only happen when navigating forward or back in an app
        _graniteNav.fullPageNavigate(_graniteNav.href());
        return;
      }

      // For remote routes, remoteModule identifies the nested app.
      // For local routes (remoteModule: undefined), fall back to the current
      // package name — the local app registers under getCurrentPackageName().
      const newActiveApp = localRouteOverrides[matchedKey].remoteModule || getCurrentPackageName();
      const active = _getActiveApp();
      if (active && active.appName === newActiveApp) {
        const relativePath = (_graniteNav.pathname() + (_graniteNav.search() || '')).slice(active.basename.length) || '/';
        active.history.replace(relativePath);
      }
    };
    return onPopstateCaptured(handler);
  }, [localRouteOverrides]);
}

/**
 * Creates a NestedHistory for the local app seeded from the current
 * browser URL, and registers it in global routing state. Call attachHistory
 * separately (in a useEffect) to connect it to the host's granite machinery.
 */
export function createLocalNestedHistory(historyBasename) {
  const basePath = findBasePath(historyBasename, _graniteNav.pathname());
  if (!basePath) {
    throw new Error(`Browser URL does not match historyBasename for the local granite app`);
  }
  const relativePath = _graniteNav.pathname().slice(basePath.basePathname.length) || '/';
  const history = createNestedHistory(basePath.basePathname, relativePath, _graniteNav.search());
  set(HISTORY_INSTANCE, history);
  set(HISTORY_BASE_PATH, basePath);
  return {
    history,
    basename: basePath.basePathname
  };
}